/**
 * Pre-authored calculation cascade templates. When a coach action fires, the store
 * builds a calculation-log entry from one of these — the FSRS-specific numbers
 * are scripted (per the design: equations are not load-bearing in the mock).
 *
 * Output entries carry the full audit shape (`kind`, `replacesEntryIds`,
 * `sourceTable`, `sourceRowId`, `actorKind`, `reasonCode`, `rationale`)
 * so the calculation log's append-only / anti-hallucination contract is
 * preserved.
 */
import type {
  ActorKind,
  BaseCalcLogEntry,
  CalculationLogEntry,
  CalculationLogEntryKind,
  CalculationRowChange,
  CalculationSource,
  ReasonCode,
  SourceTable,
} from '../types'
import { deriveEntryStatus, makeCalculationLogEntry } from './calculation-log'

/**
 * Discriminated tags the mock store passes to `buildCascadeEntry`. Each maps
 * 1:1 to a `ReasonCode` + a row-change template.
 *
 * `coach_revert` and `coach_reapply` here are the *audit* operations exposed
 * by the calculation log itself (trash + redo buttons); `reprocess_cascade`
 * is the replay emitted when redo is pressed on an applied entry.
 */
export type CascadeKind =
  | 'threshold_change'
  | 'subtopic_threshold_change'
  | 'rollup_weight_change'
  | 'preset_applied'
  | 'weekly_commitment_change'
  | 'target_date_change'
  | 'topic_inclusion_change'
  | 'subtopic_enable_toggle'
  | 'attempt_reviewed'
  | 'coach_seed'
  | 'coach_revert'
  | 'coach_reapply'
  | 'reprocess_cascade'

export interface CascadeContext {
  /** Coach (or system) name recorded as `actorName`. */
  actor: string
  /** Plain-language subject spliced into rationale/summary, e.g. a topic name. */
  subject: string
  /**
   * Entries that this new entry references in `replacesEntryIds`.
   * - For `coach_revert` / `coach_reapply` / `reprocess_cascade` — the original entry being acted on.
   * - For major-class setting changes (`threshold_change`, etc.) — empty
   *   in mock V1; production would point at the attempts being replayed.
   */
  replacesEntryIds?: string[]
  /** Override the default actor kind. Defaults to 'coach' for all CascadeKinds. */
  actorKindOverride?: ActorKind
  /**
   * Replaces the templated `rowsAffected` entirely (used by the seed tool to
   * append one audit row per touched subtopic).
   */
  rowsAffectedOverride?: CalculationRowChange[]
}

// =============================================================================
// ROW-CHANGE TEMPLATES (unchanged from prior mock)
// =============================================================================

export const CASCADE_ROW_TEMPLATES: Record<
  CascadeKind,
  CalculationRowChange[]
> = {
  threshold_change: [
    {
      targetKind: 'topic',
      targetName: '{subject}',
      field: 'minProficiencyThreshold',
      before: 80,
      after: 85,
      formulaName: 'Manual Coach Override',
      purpose:
        'Coach changed the per-topic proficiency goal the engine treats as "good enough".',
      equation: 'manual coach override',
      weights: [],
      inputs: [
        {
          name: 'reason',
          value: 'coach adjustment',
          note: 'set on the Study Plan tab',
        },
      ],
      output: 'minProficiencyThreshold updated',
    },
    {
      targetKind: 'topic',
      targetName: '{subject}',
      field: 'maxProficiencyThreshold',
      before: 85,
      after: 90,
      formulaName: 'Manual Coach Override',
      purpose:
        'Coach changed the per-topic max proficiency goal used for mastered classification.',
      equation: 'manual coach override',
      weights: [],
      inputs: [
        {
          name: 'reason',
          value: 'coach adjustment',
          note: 'set on the Study Plan tab',
        },
      ],
      output: 'maxProficiencyThreshold updated',
    },
    {
      targetKind: 'projection',
      targetName: 'Time to mastery',
      field: 'estimatedDays',
      before: 86,
      after: 90,
      formulaName: 'Time-to-Mastery Projection',
      purpose:
        'A higher goal widens the remaining gap, lengthening the projected plan.',
      equation: 'days = ceil((Σ gapᵢ · hoursPerGapPoint) / weeklyHours · 7)',
      weights: [
        {
          symbol: 'hoursPerGapPoint',
          value: '0.5',
          role: 'tunable effort constant',
        },
      ],
      inputs: [
        {
          name: 'Σ gap',
          value: 'recomputed',
          note: 'summed mastery gap across included topics',
        },
      ],
      output: 'estimatedDays recomputed',
    },
  ],
  subtopic_threshold_change: [
    {
      targetKind: 'subtopic',
      targetName: '{subject}',
      field: 'minProficiencyThreshold',
      before: 80,
      after: 85,
      formulaName: 'Manual Coach Override',
      purpose:
        'Coach changed the per-subtopic proficiency goal (R_d for this row only).',
      equation: 'R_d(min) = minProficiencyThreshold / 100',
      weights: [],
      inputs: [
        {
          name: 'reason',
          value: 'coach adjustment',
          note: 'set on the Study Plan tab (subtopic row)',
        },
      ],
      output: 'minProficiencyThreshold updated',
    },
    {
      targetKind: 'subtopic',
      targetName: '{subject}',
      field: 'maxProficiencyThreshold',
      before: 85,
      after: 90,
      formulaName: 'Manual Coach Override',
      purpose:
        'Coach changed the per-subtopic max proficiency goal (mastered threshold for this row).',
      equation: 'R_d(max) = maxProficiencyThreshold / 100',
      weights: [],
      inputs: [
        {
          name: 'reason',
          value: 'coach adjustment',
          note: 'set on the Study Plan tab (subtopic row)',
        },
      ],
      output: 'maxProficiencyThreshold updated',
    },
    {
      targetKind: 'projection',
      targetName: 'Time to mastery',
      field: 'estimatedDays',
      before: 86,
      after: 90,
      formulaName: 'Time-to-Mastery Projection',
      purpose:
        'A higher subtopic goal can widen the topic rollup gap and lengthen the plan.',
      equation: 'days = ceil((Σ gapᵢ · hoursPerGapPoint) / weeklyHours · 7)',
      weights: [
        {
          symbol: 'hoursPerGapPoint',
          value: '0.5',
          role: 'tunable effort constant',
        },
      ],
      inputs: [
        {
          name: 'Σ gap',
          value: 'recomputed',
          note: 'summed mastery gap across included topics',
        },
      ],
      output: 'estimatedDays recomputed',
    },
  ],
  rollup_weight_change: [
    {
      targetKind: 'subtopic',
      targetName: '{subject}',
      field: 'rollupWeight',
      before: 1,
      after: 2,
      formulaName: 'Manual Coach Override',
      purpose:
        'Coach changed how strongly this subtopic contributes to topic rollup bounds and mastery.',
      equation: 'manual coach override',
      weights: [],
      inputs: [
        {
          name: 'reason',
          value: 'coach adjustment',
          note: 'set on the Study Plan tab (subtopic row)',
        },
      ],
      output: 'rollupWeight updated',
    },
    {
      targetKind: 'topic',
      targetName: 'Topic rollup',
      field: 'minProficiencyThreshold',
      before: 60,
      after: 63,
      formulaName: 'Weighted Proficiency Bounds Rollup',
      purpose:
        'Changing subtopic rollup weight rebalances the topic-level min proficiency threshold.',
      equation:
        'topicMin = weightedAverage(subtopicMinᵢ, weight = rollupWeightᵢ)',
      weights: [],
      inputs: [
        {
          name: 'subtopic',
          value: '{subject}',
          note: 'weight changed for this subtopic',
        },
      ],
      output: 'topic min threshold recomputed',
    },
    {
      targetKind: 'topic',
      targetName: 'Topic rollup',
      field: 'maxProficiencyThreshold',
      before: 80,
      after: 83,
      formulaName: 'Weighted Proficiency Bounds Rollup',
      purpose:
        'Changing subtopic rollup weight rebalances the topic-level max proficiency threshold.',
      equation:
        'topicMax = weightedAverage(subtopicMaxᵢ, weight = rollupWeightᵢ)',
      weights: [],
      inputs: [
        {
          name: 'subtopic',
          value: '{subject}',
          note: 'weight changed for this subtopic',
        },
      ],
      output: 'topic max threshold recomputed',
    },
  ],
  preset_applied: [
    {
      targetKind: 'topic',
      targetName: 'Multiple topics',
      field: 'minProficiencyThreshold',
      before: 0,
      after: 0,
      formulaName: 'Manual Coach Override',
      purpose:
        'Applied a company preset, overriding several per-topic proficiency goals at once.',
      equation: 'manual coach override (bulk)',
      weights: [],
      inputs: [
        {
          name: 'preset',
          value: '{subject}',
          note: 'company proficiency preset',
        },
      ],
      output: 'multiple min thresholds updated',
    },
    {
      targetKind: 'topic',
      targetName: 'Multiple topics',
      field: 'maxProficiencyThreshold',
      before: 0,
      after: 0,
      formulaName: 'Manual Coach Override',
      purpose:
        'Applied a company preset, overriding several per-topic max proficiency goals at once.',
      equation: 'manual coach override (bulk)',
      weights: [],
      inputs: [
        {
          name: 'preset',
          value: '{subject}',
          note: 'company proficiency preset',
        },
      ],
      output: 'multiple max thresholds updated',
    },
    {
      targetKind: 'projection',
      targetName: 'Time to mastery',
      field: 'estimatedDays',
      before: 86,
      after: 94,
      formulaName: 'Time-to-Mastery Projection',
      purpose:
        'Bulk threshold changes shift the total remaining gap and the projected plan length.',
      equation: 'days = ceil((Σ gapᵢ · hoursPerGapPoint) / weeklyHours · 7)',
      weights: [
        {
          symbol: 'hoursPerGapPoint',
          value: '0.5',
          role: 'tunable effort constant',
        },
      ],
      inputs: [
        {
          name: 'Σ gap',
          value: 'recomputed',
          note: 'summed mastery gap across included topics',
        },
      ],
      output: 'estimatedDays recomputed',
    },
  ],
  weekly_commitment_change: [
    {
      targetKind: 'projection',
      targetName: 'Time to mastery',
      field: 'estimatedDays',
      before: 86,
      after: 103,
      formulaName: 'Time-to-Mastery Projection',
      purpose:
        'Fewer weekly hours stretches the same remaining work over more calendar days.',
      equation: 'days = ceil((Σ gapᵢ · hoursPerGapPoint) / weeklyHours · 7)',
      weights: [
        {
          symbol: 'hoursPerGapPoint',
          value: '0.5',
          role: 'tunable effort constant',
        },
      ],
      inputs: [
        {
          name: 'weeklyHours',
          value: '{subject}',
          note: 'updated client weekly commitment',
        },
      ],
      output: 'estimatedDays recomputed',
    },
  ],
  target_date_change: [
    {
      targetKind: 'projection',
      targetName: 'Time to mastery',
      field: 'targetDate',
      before: 0,
      after: 0,
      formulaName: 'Target Date Update',
      purpose:
        'Coach updated the deadline the projection is compared against; no engine math runs.',
      equation: 'comparison only',
      weights: [],
      inputs: [
        {
          name: 'targetDate',
          value: '{subject}',
          note: 'new comparison deadline',
        },
      ],
      output: 'targetDate updated',
    },
  ],
  topic_inclusion_change: [
    {
      targetKind: 'overall',
      targetName: 'Overall mastery',
      field: 'overallMastery',
      before: 53,
      after: 53,
      formulaName: 'Overall Mastery Rollup',
      purpose:
        'The set of topics in the plan changed, so the headline rollup is recomputed.',
      equation: 'overallMastery = mean(topicMasteryᵢ over all topics)',
      weights: [],
      inputs: [
        {
          name: 'topic',
          value: '{subject}',
          note: 'topic included/excluded from the plan',
        },
      ],
      output: 'overallMastery recomputed',
    },
    {
      targetKind: 'projection',
      targetName: 'Time to mastery',
      field: 'estimatedDays',
      before: 86,
      after: 79,
      formulaName: 'Time-to-Mastery Projection',
      purpose:
        'Including or excluding a topic changes the total remaining gap in the plan.',
      equation: 'days = ceil((Σ gapᵢ · hoursPerGapPoint) / weeklyHours · 7)',
      weights: [
        {
          symbol: 'hoursPerGapPoint',
          value: '0.5',
          role: 'tunable effort constant',
        },
      ],
      inputs: [
        {
          name: 'Σ gap',
          value: 'recomputed',
          note: 'summed mastery gap across included topics',
        },
      ],
      output: 'estimatedDays recomputed',
    },
  ],
  subtopic_enable_toggle: [
    {
      targetKind: 'topic',
      targetName: 'Topic rollup',
      field: 'topicMastery',
      before: 60,
      after: 60,
      formulaName: 'Topic Mastery Rollup',
      purpose:
        'Coach enabled/disabled a subtopic, changing which rows feed the topic rollup.',
      equation:
        'topicMastery = Σ(wᵢ · effectiveMasteryᵢ) / Σ(wᵢ),  wᵢ = 101 − effectiveMasteryᵢ',
      weights: [],
      inputs: [
        {
          name: 'subtopic',
          value: '{subject}',
          note: 'subtopic included/excluded from the rollup',
        },
      ],
      output: 'topicMastery recomputed',
    },
  ],
  attempt_reviewed: [
    {
      targetKind: 'subtopic',
      targetName: '{subject}',
      field: 'mastery',
      before: 64,
      after: 67,
      formulaName: 'EMA Mastery Update',
      purpose:
        'The coach reviewed the attempt, so its graded signal now feeds the knowledge map.',
      equation: "mastery' = (1 − α) · mastery + α · attemptScore",
      weights: [{ symbol: 'α', value: '0.15', role: 'EMA smoothing factor' }],
      inputs: [
        {
          name: 'attemptScore',
          value: 'from review',
          note: 'derived from the reviewed grade + approach',
        },
      ],
      output: 'mastery recomputed',
    },
    {
      targetKind: 'topic',
      targetName: 'Topic rollup',
      field: 'topicMastery',
      before: 59,
      after: 61,
      formulaName: 'Topic Mastery Rollup',
      purpose: 'Re-aggregates the topic now that one subtopic moved.',
      equation:
        'topicMastery = Σ(wᵢ · effectiveMasteryᵢ) / Σ(wᵢ),  wᵢ = 101 − effectiveMasteryᵢ',
      weights: [
        {
          symbol: 'wᵢ',
          value: '101 − EMᵢ',
          role: 'weakness weight per subtopic',
        },
      ],
      inputs: [
        {
          name: 'subtopics',
          value: 'recomputed',
          note: 'topic re-aggregated from subtopics',
        },
      ],
      output: 'topicMastery recomputed',
    },
    {
      targetKind: 'overall',
      targetName: 'Overall mastery',
      field: 'overallMastery',
      before: 53,
      after: 54,
      formulaName: 'Overall Mastery Rollup',
      purpose: 'Headline progress number recomputed after the topic moved.',
      equation: 'overallMastery = mean(topicMasteryᵢ over all topics)',
      weights: [],
      inputs: [
        {
          name: 'topics',
          value: '29',
          note: 'all topics included in the rollup',
        },
      ],
      output: 'overallMastery recomputed',
    },
  ],
  coach_seed: [
    {
      targetKind: 'overall',
      targetName: 'Overall mastery',
      field: 'overallMastery',
      before: 0,
      after: 0,
      formulaName: 'Knowledge Map Seed',
      purpose:
        'Coach seeded the client\u2019s knowledge map with starting bucket + per-row overrides.',
      equation: 'seeded values (no formula)',
      weights: [],
      inputs: [
        {
          name: 'bucket',
          value: '{subject}',
          note: 'starting mastery bucket selected by the coach',
        },
      ],
      output: 'knowledge map seeded',
    },
  ],
  coach_revert: [
    {
      targetKind: 'overall',
      targetName: 'Audit',
      field: 'status',
      before: 0,
      after: 0,
      formulaName: 'Append-Only Revert',
      purpose:
        'Coach reverted an earlier calculation. The original row is unchanged; this new entry records the revert.',
      equation: 'append-only audit event',
      weights: [],
      inputs: [
        {
          name: 'target',
          value: '{subject}',
          note: 'the calc-log entry being reverted',
        },
      ],
      output: 'original entry marked reverted (derived)',
    },
  ],
  coach_reapply: [
    {
      targetKind: 'overall',
      targetName: 'Audit',
      field: 'status',
      before: 0,
      after: 0,
      formulaName: 'Append-Only Re-Apply',
      purpose:
        'Coach re-applied a previously reverted calculation. A fresh entry is appended; the original revert event is preserved.',
      equation: 'append-only audit event',
      weights: [],
      inputs: [
        {
          name: 'target',
          value: '{subject}',
          note: 'the calc-log entry being re-applied',
        },
      ],
      output: 'fresh calculation appended',
    },
  ],
  reprocess_cascade: [
    {
      targetKind: 'overall',
      targetName: 'Audit',
      field: 'status',
      before: 0,
      after: 0,
      formulaName: 'Reprocess Cascade',
      purpose:
        'Coach requested a recalculation; the engine would replay original attempt events under current settings.',
      equation:
        'append-only audit event (would trigger full replay in production)',
      weights: [],
      inputs: [
        {
          name: 'target',
          value: '{subject}',
          note: 'the calc-log entry triggering the cascade',
        },
      ],
      output: 'reprocess scheduled',
    },
  ],
}

// =============================================================================
// META — maps each CascadeKind to its audit fields
// =============================================================================

interface CascadeMeta {
  source: CalculationSource
  sourceTable: SourceTable
  reasonCode: ReasonCode
  rationale: string
  summary: string
  defaultActorKind: ActorKind
}

const KIND_META: Record<CascadeKind, CascadeMeta> = {
  threshold_change: {
    source: 'coach_adjustment',
    sourceTable: 'CoachAdjustmentLog',
    reasonCode: 'threshold_change',
    rationale: 'Coach changed a topic proficiency goal on the Study Plan tab.',
    summary: 'Proficiency goal for {subject} changed — projection recomputed.',
    defaultActorKind: 'coach',
  },
  subtopic_threshold_change: {
    source: 'coach_adjustment',
    sourceTable: 'CoachAdjustmentLog',
    reasonCode: 'threshold_change',
    rationale:
      'Coach changed a subtopic proficiency goal on the Study Plan tab.',
    summary:
      'Subtopic proficiency goal for {subject} changed — projection recomputed.',
    defaultActorKind: 'coach',
  },
  rollup_weight_change: {
    source: 'coach_adjustment',
    sourceTable: 'CoachAdjustmentLog',
    reasonCode: 'rollup_weight_change',
    rationale: 'Coach changed a subtopic rollup weight on the Study Plan tab.',
    summary:
      'Subtopic rollup weight for {subject} changed — topic bounds and projection recomputed.',
    defaultActorKind: 'coach',
  },
  preset_applied: {
    source: 'coach_adjustment',
    sourceTable: 'CoachAdjustmentLog',
    reasonCode: 'preset_applied',
    rationale: 'Coach applied a company proficiency preset.',
    summary:
      'Applied the {subject} preset — multiple thresholds and the projection recomputed.',
    defaultActorKind: 'coach',
  },
  weekly_commitment_change: {
    source: 'coach_adjustment',
    sourceTable: 'CoachAdjustmentLog',
    reasonCode: 'weekly_commitment_change',
    rationale: 'Coach changed the client weekly commitment.',
    summary: 'Weekly commitment set to {subject}h — projection recomputed.',
    defaultActorKind: 'coach',
  },
  target_date_change: {
    source: 'coach_adjustment',
    sourceTable: 'CoachAdjustmentLog',
    reasonCode: 'target_date_change',
    rationale: 'Coach updated the projected-finish comparison deadline.',
    summary: 'Target date set to {subject} — fits indicator recomputed.',
    defaultActorKind: 'coach',
  },
  topic_inclusion_change: {
    source: 'coach_adjustment',
    sourceTable: 'CoachAdjustmentLog',
    reasonCode: 'topic_inclusion_change',
    rationale: 'Coach changed which topics are in the study plan.',
    summary:
      'Plan membership changed for {subject} — overall mastery and projection recomputed.',
    defaultActorKind: 'coach',
  },
  subtopic_enable_toggle: {
    source: 'coach_adjustment',
    sourceTable: 'CoachAdjustmentLog',
    reasonCode: 'subtopic_enable_toggle',
    rationale: 'Coach toggled whether a subtopic feeds the topic rollup.',
    summary: 'Subtopic {subject} enable toggled — topic rollup recomputed.',
    defaultActorKind: 'coach',
  },
  attempt_reviewed: {
    source: 'coach_adjustment',
    sourceTable: 'CoachAdjustmentLog',
    reasonCode: 'attempt_reviewed',
    rationale: 'Coach marked a completed attempt as reviewed.',
    summary:
      'Reviewed attempt on {subject} — subtopic, topic, and overall mastery recomputed.',
    defaultActorKind: 'coach',
  },
  coach_seed: {
    source: 'seed',
    sourceTable: 'CoachAdjustmentLog',
    reasonCode: 'coach_seed',
    rationale:
      'Coach seeded the client\u2019s knowledge map with starting bucket + overrides.',
    summary:
      'Seeded knowledge map with bucket = {subject} — initial mastery, stability, and difficulty set per subtopic.',
    defaultActorKind: 'coach',
  },
  coach_revert: {
    source: 'coach_adjustment',
    sourceTable: 'CoachAdjustmentLog',
    reasonCode: 'coach_revert',
    rationale:
      'Coach reverted an earlier calculation from the Calculation Log.',
    summary: 'Reverted calculation: {subject}',
    defaultActorKind: 'coach',
  },
  coach_reapply: {
    source: 'coach_adjustment',
    sourceTable: 'CoachAdjustmentLog',
    reasonCode: 'coach_reapply',
    rationale:
      'Coach re-applied a previously reverted calculation from the Calculation Log.',
    summary: 'Re-applied calculation: {subject}',
    defaultActorKind: 'coach',
  },
  reprocess_cascade: {
    source: 'coach_adjustment',
    sourceTable: 'CoachAdjustmentLog',
    reasonCode: 'reprocess_cascade',
    rationale:
      'Coach requested a recalculation; the knowledge map would be re-derived from original events in production.',
    summary:
      'Recalculation requested for {subject} — knowledge map re-derived.',
    defaultActorKind: 'coach',
  },
}

// =============================================================================
// BUILDER
// =============================================================================

/**
 * Builds a calculation-log entry (minus id/occurredAt — `makeCalculationLogEntry`
 * fills those). The output carries the full audit shape, including
 * `kind` and `replacesEntryIds`.
 *
 * Audit-kind entries (`coach_revert`, `coach_reapply`, `reprocess_cascade`)
 * are emitted as `kind: 'original'` — they ARE the audit fact, not a replay
 * of one. The thing they POINT AT is what flips status under
 * `deriveEntryStatus`.
 */
export function buildCascadeEntry(
  kind: CascadeKind,
  ctx: CascadeContext,
): Omit<CalculationLogEntry, 'id' | 'occurredAt'> {
  const meta = KIND_META[kind]
  const fill = (s: string) => s.replaceAll('{subject}', ctx.subject)
  const rowsAffected =
    ctx.rowsAffectedOverride ??
    CASCADE_ROW_TEMPLATES[kind].map((row) => ({
      ...row,
      targetName: fill(row.targetName),
      inputs: row.inputs.map((i) => ({ ...i, value: fill(i.value) })),
    }))

  // Reprocess cascade is the only kind that ships as kind='replay' (it IS
  // the replay event in the log). Everything else — including the audit
  // ops coach_revert / coach_reapply — is a kind='original' fact.
  const entryKind: CalculationLogEntryKind =
    kind === 'reprocess_cascade' ? 'replay' : 'original'

  // A synthesized id-stub the caller's `makeCalculationLogEntry` will
  // replace; we still need it locally to compose `sourceRowId`. The real
  // id is assigned downstream.
  const idStub = `pending_${meta.reasonCode}`

  const base: Omit<BaseCalcLogEntry, 'id' | 'occurredAt'> = {
    source: meta.source,
    kind: entryKind,
    replacesEntryIds: ctx.replacesEntryIds ?? [],
    sourceTable: meta.sourceTable,
    sourceRowId: `${meta.sourceTable}:${idStub}`,
    actorKind: ctx.actorKindOverride ?? meta.defaultActorKind,
    actorName: ctx.actor,
    reasonCode: meta.reasonCode,
    rationale: meta.rationale,
    summary: fill(meta.summary),
    rowsAffected,
  }

  // All cascade kinds in V1 emit coach_adjustment / seed entries — never
  // attempt entries (those come from real student submissions). Narrowing
  // to the union member that matches `source`.
  if (meta.source === 'seed') {
    return { ...base, source: 'seed' }
  }
  return { ...base, source: 'coach_adjustment' }
}

export { deriveEntryStatus, makeCalculationLogEntry }
